import React, { useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import GavelIcon from '@mui/icons-material/Gavel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LinearProgress from '@mui/material/LinearProgress';
import SearchIcon from '@mui/icons-material/Search';
import DescriptionIcon from '@mui/icons-material/Description';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

interface PanoramaProgressProps {
  phase: string;
  progress: number;
  nSearches?: number;
  nDocs?: number;
  nTheses?: number;
  inputTokens?: number;
  outputTokens?: number;
}

// Espelha o padrao visual do DeepResearchProgress, com as fases do Panorama Juridico.
const PHASE_LABELS: Record<string, string> = {
  phase1: 'Analisando o caso e mapeando os temas',
  phase1_done: 'Temas mapeados',
  workers: 'Pesquisando jurisprudência por tema',
  citations: 'Resolvendo os precedentes citados',
  dedup: 'Consolidando as teses',
  planner: 'Montando o plano da peça',
  export: 'Organizando as fontes e o índice',
  done: 'Panorama concluído',
  error: 'Falha no panorama',
};

const formatTokenCount = (n: number): string => {
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'k';
  return n.toString();
};

const PanoramaProgress: React.FC<PanoramaProgressProps> = ({
  phase,
  progress,
  nSearches = 0,
  nDocs = 0,
  nTheses = 0,
  inputTokens = 0,
  outputTokens = 0,
}) => {
  const [expanded, setExpanded] = useState(false);
  const isDone = phase === 'done';
  const isError = phase === 'error';
  const phaseLabel = PHASE_LABELS[phase] || phase;
  const pct = Math.max(2, Math.round((progress || 0) * 100));
  const hasStats = nSearches > 0 || nDocs > 0 || nTheses > 0 || inputTokens > 0 || outputTokens > 0;

  return (
    <Row>
      <Header $clickable={hasStats} onClick={() => hasStats && setExpanded(v => !v)}>
        <IconWrap $pending={!isDone && !isError}>
          {isDone
            ? <CheckCircleIcon style={{ fontSize: 15, color: '#10b981' }} />
            : <GavelIcon style={{ fontSize: 15 }} />}
        </IconWrap>
        <Verb>Panorama Jurídico{(isDone || isError) ? '' : ` — ${phaseLabel.toLowerCase()}`}</Verb>
        {hasStats && (
          <Stats>
            {nSearches > 0 && <span><SearchIcon style={{ fontSize: 12 }} /> {nSearches}</span>}
            {nDocs > 0 && <span><DescriptionIcon style={{ fontSize: 12 }} /> {nDocs}</span>}
            {nTheses > 0 && <span><GavelIcon style={{ fontSize: 12 }} /> {nTheses}</span>}
            {(inputTokens > 0 || outputTokens > 0) && (
              <>
                <span>↑ {formatTokenCount(inputTokens)}</span>
                <span>↓ {formatTokenCount(outputTokens)}</span>
              </>
            )}
          </Stats>
        )}
        {hasStats && (
          <ChevronWrap>
            {expanded ? <ExpandLessIcon style={{ fontSize: 16 }} /> : <ExpandMoreIcon style={{ fontSize: 16 }} />}
          </ChevronWrap>
        )}
      </Header>
      {!isDone && !isError && (
        <ProgressLine>
          <LinearProgress variant="determinate" value={pct} />
        </ProgressLine>
      )}
      {!isDone && !isError && <Hint>A pesquisa do panorama pode levar alguns minutos.</Hint>}
      {expanded && (
        <Details>
          <DetailLine>
            <DetailIcon><SearchIcon style={{ fontSize: 13 }} /></DetailIcon>
            <DetailLabel><strong>{nSearches}</strong> buscas</DetailLabel>
            <DetailDesc>consultas a jurisprudência e legislação</DetailDesc>
          </DetailLine>
          <DetailLine>
            <DetailIcon><DescriptionIcon style={{ fontSize: 13 }} /></DetailIcon>
            <DetailLabel><strong>{nDocs}</strong> documentos</DetailLabel>
            <DetailDesc>acórdãos e normas recuperados e lidos</DetailDesc>
          </DetailLine>
          <DetailLine>
            <DetailIcon><GavelIcon style={{ fontSize: 13 }} /></DetailIcon>
            <DetailLabel><strong>{nTheses}</strong> teses</DetailLabel>
            <DetailDesc>teses extraídas das fontes durante a pesquisa</DetailDesc>
          </DetailLine>
          <DetailLine>
            <DetailIcon>↑</DetailIcon>
            <DetailLabel><strong>{formatTokenCount(inputTokens)}</strong> tokens de entrada</DetailLabel>
            <DetailDesc>texto enviado ao modelo (todas as fases e workers)</DetailDesc>
          </DetailLine>
          <DetailLine>
            <DetailIcon>↓</DetailIcon>
            <DetailLabel><strong>{formatTokenCount(outputTokens)}</strong> tokens de saída</DetailLabel>
            <DetailDesc>texto gerado pelo modelo durante a pesquisa</DetailDesc>
          </DetailLine>
        </Details>
      )}
    </Row>
  );
};

export default PanoramaProgress;

const pulse = keyframes`
  0%, 100% { opacity: 0.55; }
  50%      { opacity: 0.95; }
`;

const Row = styled.div`
  display: flex;
  flex-direction: column;
  margin: 4px 0;
  font-family: 'Sora', 'Lexend', sans-serif;
`;

const Header = styled.div<{ $clickable: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.font_color || '#666'};
  opacity: 0.9;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  user-select: none;
  flex-wrap: wrap;
`;

const IconWrap = styled.span<{ $pending: boolean }>`
  display: inline-flex;
  align-items: center;
  opacity: 0.85;
  color: #10b981;
  ${({ $pending }) => $pending && css`animation: ${pulse} 1.6s ease-in-out infinite;`}
`;

const Verb = styled.span`
  font-weight: 500;
`;

const Stats = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  opacity: 0.7;
  font-size: 0.78rem;
  & > span { display: inline-flex; align-items: center; gap: 3px; }
`;

const ChevronWrap = styled.span`
  display: inline-flex;
  align-items: center;
  opacity: 0.55;
`;

const ProgressLine = styled.div`
  margin: 6px 0 2px;
  max-width: 480px;
  && .MuiLinearProgress-root {
    height: 3px;
    border-radius: 2px;
    background: ${({ theme }) => theme.borderGrey || '#e0e0e0'};
  }
  && .MuiLinearProgress-bar {
    border-radius: 2px;
    background: #10b981;
  }
`;

const Hint = styled.span`
  font-size: 0.72rem;
  opacity: 0.45;
  color: ${({ theme }) => theme.font_color || '#999'};
  margin-top: 4px;
`;

const Details = styled.div`
  margin-top: 8px;
  margin-left: 22px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DetailLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-size: 0.78rem;
  opacity: 0.8;
  line-height: 1.5;
`;

const DetailIcon = styled.span`
  display: inline-flex;
  align-items: center;
  opacity: 0.7;
  width: 16px;
  flex-shrink: 0;
`;

const DetailLabel = styled.span`
  min-width: 120px;
  flex-shrink: 0;
`;

const DetailDesc = styled.span`
  opacity: 0.65;
  word-break: break-word;
`;
